import { Config } from '../config/Config';
import AsyncStorage from './AsyncStorage';

// ── Session-level cache for static/rarely-changing data ──────────────────────
// Departments and sessions don't change during a session — cache them so
// 8 pages calling getDeptartments don't each fire a separate DB connection.
const _cache = {};

export const getCachedDepartments = async (baseUrl) => {
  if (_cache.departments) return _cache.departments;
  const data = await InvokeApi(baseUrl + apiList.getDeptartments, { status: 'Active' });
  if (data?.department) _cache.departments = data.department;
  return _cache.departments || [];
};

export const getCachedSessions = async (baseUrl) => {
  if (_cache.sessions) return _cache.sessions;
  const data = await InvokeApi(baseUrl + apiList.getSessions, {});
  if (data?.session) _cache.sessions = data.session;
  return _cache.sessions || [];
};

// Call this when departments are added/deleted so cache refreshes
export const invalidateDeptCache = () => { delete _cache.departments; };
export const invalidateSessionCache = () => { delete _cache.sessions; };

export const InvokeApi = async (url, params = {}) => {
  try {
    const token = await AsyncStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching data:', error);
    return { code: '500', message: 'Network error. Please check your connection.' };
  }
};

export const postImg = async (url, formData) => {
  try {
    const token = await AsyncStorage.getItem('token');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    const data = await response.json();
    return data; // always return so caller can check code and message
  } catch (error) {
    console.error('Error uploading:', error);
    return { code: '500', message: error.message };
  }
};

export const apiList = {
  employeeLogin: '/employeeLogin',
  adminLogin: '/adminLogin',

  // Employees
  addEmployeeProfileImage: '/addEmployeeProfileImage',
  addEmployee: '/addEmployee',
  getEmployees: '/getEmployees',
  getEmployee: '/getEmployee',
  getEmployeesByDepartment: '/getEmployeesByDepartment',
  editEmployee: '/editEmployee',
  deleteEmployee: '/deleteEmployee',

  // Users
  addUserProfileImage: '/addUserProfileImage',
  addUser: '/addUser',
  getUser: '/getUser',
  getUsers: '/getUsers',
  editUser: '/editUser',

  // Attendance
  addAttendance: '/addAttendance',
  getAttendance: '/getAttendance',
  getAttendanceByDate: '/getAttendanceByDate',
  getAttendanceByDateDept: '/getAttendanceByDateDept',
  getDailyAttendanceSummary: '/getDailyAttendanceSummary',
  getMonthlyAttendanceDetail: '/getMonthlyAttendanceDetail',
  getAttendanceDeductions: '/getAttendanceDeductions',
  reduceEmployeeDeduction: '/reduceEmployeeDeduction',
  reduceBulkEmployeeDeductions: '/reduceBulkEmployeeDeductions',
  getEmployeeDeductionReductions: '/getEmployeeDeductionReductions',
  getAttendanceByMonth: '/getAttendanceByMonth',
  getAttendanceCounts: '/getAttendanceCounts',
  markAbsent: '/markAbsent',
  markAttendance: '/markAttendance',
  markWeekOff: '/markWeekOff',

  // Leave
  addLeave: '/addLeave',
  getLeavesEmployee: '/getLeavesEmployee',
  getLeavesEmployees: '/getLeavesEmployees',
  approveLeavesEmployee: '/approveLeavesEmployee',
  deleteLeavesEmployee: '/deleteLeavesEmployee',

  // Holidays
  addHoliday: '/addHoliday',
  getHolidays: '/getHolidays',
  getHolidaysMonth: '/getHolidaysMonth',

  // Salary
  addFeesRecipt: '/addFeesRecipt',
  addFees: '/addFees',
  getFees: '/getFees',
  deleteFees: '/deleteFees',
  getFeesBalance: '/getFeesBalance',
  getFeesDate: '/getFeesDate',
  getEmployeeSalaryOverview: '/getEmployeeSalaryOverview',
  bulkApproveSalary: '/bulkApproveSalary',

  // Departments & Sessions
  addDeptartmentSection: '/addDeptartmentSection',
  getDeptartments: '/getDeptartments',
  deleteDepartment: '/deleteDepartment',
  getSessions: '/getSessions',
  getSession: '/getSession',
  addSession: '/addSession',
  deleteSession: '/deleteSession',

  // Notifications
  getEmployeeNotifications: '/getEmployeeNotifications',
  sendEmployeeNotification: '/sendEmployeeNotification',
  sendBulkNotificationsAll: '/sendBulkNotificationsAll',

  // Excel
  exportExcel: '/exportExcel',
  getEmployeeNotificationsByDate: '/getEmployeeNotificationsByDate',
  AddProfileImgStdPy: '/save-employee-pic-to-db',

  // Bank Details
  addBankDetails: '/addBankDetails',
  getBankDetails: '/getBankDetails',

  // Salary Structure & Calculation
  addSalaryStructure: '/addSalaryStructure',
  getSalaryStructure: '/getSalaryStructure',
  calculateMonthlySalary: '/calculateMonthlySalary',

  // Multi-Punch & Flexible Hours
  addPunch: '/addPunch',
  getPunches: '/getPunches',
  deletePunch: '/deletePunch',
  setEmployeeRequiredHours: '/setEmployeeRequiredHours',

  // Fines
  addFine: '/addFine',
  getFines: '/getFines',
  deleteFine: '/deleteFine',

  // Audit Logs
  getAuditLogs: '/getAuditLogs',

  // Scan Logs (face recognition)
  addScanLog: '/addScanLog',
  getScanLogs: '/getScanLogs',
  getScanLogsByDate: '/getScanLogsByDate',

  // Employee Documents
  addEmployeeDocument: '/addEmployeeDocument',
  getEmployeeDocuments: '/getEmployeeDocuments',
  deleteEmployeeDocument: '/deleteEmployeeDocument',
};
